// components/SmartBookingSection.jsx
import React from "react";
import "./SmartBookingSection.css";

import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import LocalTaxiRoundedIcon from "@mui/icons-material/LocalTaxiRounded";
import HotelRoundedIcon from "@mui/icons-material/HotelRounded";

import {
  GoogleMap,
  MarkerF,
  PolylineF,
  useJsApiLoader,
} from "@react-google-maps/api";

// Coordinates
const center = { lat: 21.4225, lng: 39.8262 }; // Masjid al-Haram
const jeddahAirport = { lat: 21.6702, lng: 39.1528 };
const hotel = { lat: 21.4195, lng: 39.8255 };
const haram = center;

const routePath = [jeddahAirport, hotel, haram];

function SmartBookingSection() {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyAOGi6Lrm0NrvFD6iuX7odfmhl_SM7qY8U",
  });

  return (
    <section className="section section-alt" id="booking">
      <div className="section-inner">

        {/* HEADER */}
        <div className="section-header">
          {/* <p className="section-kicker">Smart Planning</p> */}
          <h2 className="section-title">Smarter bookings for your journey</h2>
          <p className="section-subtitle">
            Tawaf makes travel and stay effortless — helping you discover flights,
            transport options, and hotels that match your schedule, budget, and
            day-to-day pilgrimage needs.
          </p>
        </div>

        <div className="booking-layout">

          {/* LEFT: MAP */}
          <div className="booking-map-panel">
            <div className="booking-map-header">
              <div className="booking-map-title">
                <span className="booking-pill booking-pill-main">
                  <FlightTakeoffRoundedIcon fontSize="small" />
                  Route preview
                </span>

                <span className="booking-pill booking-pill-soft">
                  ✈️ Airport · 🏨 Hotel · 🕋 Haram
                </span>
              </div>
              <p className="booking-map-sub">
                Visualize your airport arrival, hotel location, and routes to the Haram.
              </p>
            </div>

            <div className="booking-map-inner">
              {loadError && (
                <div className="booking-map-fallback">
                  <p>Map preview unavailable.</p>
                  <p className="booking-map-fallback-sub">
                    You’ll still be able to browse routes inside the app.
                  </p>
                </div>
              )}

              {isLoaded && !loadError && (
                <>
                  <GoogleMap
                    center={center}
                    zoom={11}
                    mapContainerClassName="booking-map-container"
                    options={{
                      disableDefaultUI: true,
                      zoomControl: false,
                      fullscreenControl: false,
                    }}
                  >
                    {/* Route polyline */}
                    <PolylineF
                      path={routePath}
                      options={{
                        strokeColor: "#f2a93b",
                        strokeOpacity: 0.95,
                        strokeWeight: 4,
                        icons: [
                          {
                            icon: {
                              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                              scale: 3,
                              strokeColor: "#cc7f00",
                            },
                            offset: "50%",
                          },
                        ],
                      }}
                    />

                    <MarkerF position={jeddahAirport} label="✈️" title="Jeddah Airport" />
                    <MarkerF position={hotel} label="🏨" title="Your Hotel" />
                    <MarkerF position={haram} label="🕋" title="Masjid al-Haram" />
                  </GoogleMap>

                  {/* MAP OVERLAY */}
                  <div className="booking-map-overlay">
                    <div className="booking-overlay-card">
                      <p className="booking-overlay-title">Your journey at a glance</p>

                      <div className="booking-overlay-grid">
                        <div className="booking-overlay-item">
                          <span className="booking-overlay-emoji">✈️</span>
                          <span className="booking-overlay-text">
                            Long-distance flights matched to your dates and route.
                          </span>
                        </div>

                        <div className="booking-overlay-item">
                          <span className="booking-overlay-emoji">🚕</span>
                          <span className="booking-overlay-text">
                            Local taxis & shuttles between airport, hotel & Haram.
                          </span>
                        </div>

                        <div className="booking-overlay-item">
                          <span className="booking-overlay-emoji">🏨</span>
                          <span className="booking-overlay-text">
                            Hotels near the Haram with clear walking distance.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* RIGHT: BOOKING CARDS */}
          <div className="booking-side">

            {/* FLIGHTS */}
            <div className="booking-card booking-card-elevated">
              <div className="booking-body">
                <div className="booking-label">
                  <FlightTakeoffRoundedIcon className="booking-icon" />
                  Long-distance travel
                </div>
                <p>
                  Find flights that perfectly match your departure city, dates,
                  and budget — optimized for your pilgrimage timeline.
                </p>
                <div className="booking-meta">
                  <span>Global routes</span>
                  <span>Personalized</span>
                </div>
              </div>
            </div>

            {/* LOCAL TRANSPORT */}
            <div className="booking-card booking-card-elevated">
              <div className="booking-body">
                <div className="booking-label">
                  <LocalTaxiRoundedIcon className="booking-icon" />
                  Local transport
                </div>
                <p>
                  Access taxis, shuttles, and metro options based on where you
                  are — ensuring safe and simple movement between key sites.
                </p>
                <div className="booking-meta">
                  <span>Makkah • Madinah</span>
                  <span>Live routes</span>
                </div>
              </div>
            </div>

            {/* HOTELS */}
            <div className="booking-card booking-card-elevated">
              <div className="booking-body">
                <div className="booking-label">
                  <HotelRoundedIcon className="booking-icon" />
                  Stay near holy sites
                </div>
                <p>
                  Discover hotels close to the Haram with walking distance,
                  prayer-time access, and group meeting points clearly shown.
                </p>
                <div className="booking-meta">
                  <span>Comfort & proximity</span>
                  <span>Verified stays</span>
                </div>
              </div>
            </div>

            <div className="booking-note">
              Tawaf helps you choose what truly
              fits your journey and pace.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SmartBookingSection;
